import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import anime from "animejs";
import {
  startIosSafariFullscreenHandler,
  resetIosFullscreenState,
} from "../../../utilities";
import { PixiSettings } from "../../../pixiSettings";

// Constants
const FADE_OUT_DURATION = 150; // Fast fade-out for instant feel

// Device/orientation-specific scale multipliers
const SCALE_MULTIPLIERS = {
  MOBILE: {
    LANDSCAPE: 2.5, // Mobile landscape
  },
  TABLET: {
    LANDSCAPE: 1.0, // Tablet landscape
  },
};

/**
 * IosFullscreenOverlayController - Manages iOS Safari fullscreen overlay
 * Shows spine animation with swipe-up gesture hint
 * Triggers Safari UI hiding on successful swipe
 */
class IosFullscreenOverlayController {
  constructor(container, dispatch, dimensions, symbolWidth, symbolHeight) {
    this.container = container;
    this.dispatch = dispatch;
    this.dimensions = dimensions;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;

    this.spine = null;
    this.spineContainer = null;
    this.initialized = false;
    this.isCleanedUp = false;

    // Event handler cleanup function
    this.handlerCleanup = null;

    // Animation reference
    this.fadeOutAnimationRef = null;

    this.initializeSpine();
  }

  /**
   * Initialize spine animation
   */
  initializeSpine() {
    try {
      // Create spine container
      this.spineContainer = new PIXI.Container();
      this.spineContainer.name = "ios_fullscreen_overlay_spine_container";
      this.spineContainer.sortableChildren = true;
      this.spineContainer.zIndex = 20000;
      this.container.addChild(this.spineContainer);

      // Create spine
      this.spine = this.createSpine();
      if (!this.spine) {
        return;
      }

      this.spineContainer.addChild(this.spine);
      this.spine.zIndex = 20000;

      // Apply scale and center
      this.applyScaleAndCenter();

      // Warm up spine for instant display
      this.initializeSpineStructure();

      this.initialized = true;
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Create spine from loaded assets
   */
  createSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "ios_scroll-skeleton";
      const atlasAlias = "ios_scroll-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!spine || !spine.skeleton || !spine.state) {
        return null;
      }

      return spine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Initialize skeleton by setting an animation to populate spine structure
   * Warm up technique for instant display
   */
  initializeSpineStructure() {
    if (!this.spine || !this.spine.state) {
      return;
    }

    try {
      const animations = this.spine.skeleton?.data?.animations?.map(
        (anim) => anim.name,
      );

      if (animations && animations.length > 0) {
        // Set first animation
        const animationToUse = animations[0];
        this.spine.state.setAnimation(0, animationToUse, false);

        // Update one frame to populate structure
        this.spine.state.update(0.016);

        // Update world transform
        try {
          if (this.spine.skeleton.physics !== undefined) {
            this.spine.skeleton.updateWorldTransform();
          }
        } catch (transformError) {
          // Skip if physics not initialized
        }

        // Clear tracks to reset
        this.spine.state.clearTracks();
      }

      // Set visibility - start hidden, will show when show() is called
      this.spine.alpha = 0;
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Get device-specific scale multiplier for spine animation
   * Allows fine-tuning of spine size per device/orientation
   */
  getDeviceScaleMultiplier() {
    const { device, orientation } = this.dimensions;

    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.LANDSCAPE) {
        return SCALE_MULTIPLIERS.MOBILE.LANDSCAPE;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.LANDSCAPE) {
        return SCALE_MULTIPLIERS.TABLET.LANDSCAPE;
      }
    }

    // Default multiplier
    return 1.0;
  }

  /**
   * Apply proportional scaling and centering to spine
   * Scale to cover entire screen (like background-size: cover)
   */
  applyScaleAndCenter() {
    if (!this.spine || !this.dimensions) return;

    // Center the spine in the screen
    const centerX = this.dimensions.width / 2;
    const centerY = this.dimensions.height / 2;

    // Use MAX instead of MIN to ensure full coverage (like background-size: cover)
    // This will scale up to fill the screen completely, even if some parts are cut off
    const baseScale = Math.max(
      this.dimensions.width / 1920,
      this.dimensions.height / 1080,
    );

    // Apply device/orientation-specific multiplier
    const deviceMultiplier = this.getDeviceScaleMultiplier();
    const scale = baseScale * deviceMultiplier;

    this.spine.scale.set(scale);
    this.spine.x = centerX;
    this.spine.y = centerY;
  }

  /**
   * Called when fullscreen is achieved
   */
  onFullscreenAchieved() {
    sessionStorage.setItem("iosFullscreenDismissed", "true");

    const canvas = document.querySelector("canvas");
    if (canvas) {
      canvas.style.pointerEvents = "";
      canvas.style.touchAction = "";
    }

    window.dispatchEvent(new Event("resize"));
    this.fadeOut();
  }

  /**
   * Show overlay and start fullscreen handler
   */
  show() {
    if (!this.initialized || this.isCleanedUp) return;

    const canvas = document.querySelector("canvas");
    if (canvas) {
      canvas.style.pointerEvents = "none";
      canvas.style.touchAction = "pan-y";
    }

    if (this.spine) {
      this.spine.alpha = 1;
    }

    if (this.spine && this.spine.state) {
      try {
        const animations = this.spine.skeleton?.data?.animations?.map(
          (anim) => anim.name,
        );
        if (animations && animations.length > 0) {
          this.spine.state.setAnimation(0, animations[0], true);
        }
      } catch (error) {
        // Silent fail
      }
    }

    this.handlerCleanup = startIosSafariFullscreenHandler((needsScroll) => {
      if (needsScroll) {
        // Toolbar reappeared - show overlay again
        const {
          openIosFullscreenOverlay,
        } = require("../../../State/Slices/userInterfaceSlice");
        this.dispatch(openIosFullscreenOverlay());
      } else {
        // Fullscreen achieved - hide animation
        this.onFullscreenAchieved();
      }
    });
  }

  /**
   * Hide overlay instantly
   */
  hide() {
    if (!this.spine) return;

    const canvas = document.querySelector("canvas");
    if (canvas) {
      canvas.style.pointerEvents = "";
      canvas.style.touchAction = "";
    }

    if (this.handlerCleanup) {
      this.handlerCleanup();
      this.handlerCleanup = null;
    }

    if (this.fadeOutAnimationRef) {
      this.fadeOutAnimationRef.pause();
      this.fadeOutAnimationRef = null;
    }

    if (this.spine) {
      this.spine.alpha = 0;
    }

    // Stop spine animation
    if (this.spine && this.spine.state) {
      try {
        this.spine.state.clearTracks();
      } catch (error) {
        // Silent fail
      }
    }
  }

  /**
   * Fade out overlay
   */
  fadeOut() {
    if (!this.spine) return;

    // Restore canvas interactivity
    const canvas = document.querySelector("canvas");
    if (canvas) {
      canvas.style.pointerEvents = "";
      canvas.style.touchAction = "";
    }

    // Cancel any existing animation
    if (this.fadeOutAnimationRef) {
      this.fadeOutAnimationRef.pause();
    }

    // Fade out animation on spine
    this.fadeOutAnimationRef = anime({
      targets: this.spine,
      alpha: 0,
      duration: FADE_OUT_DURATION,
      easing: "easeOutQuad",
      complete: () => {
        // Dispatch close action
        const {
          closeIosFullscreenOverlay,
        } = require("../../../State/Slices/userInterfaceSlice");
        this.dispatch(closeIosFullscreenOverlay());
      },
    });
  }

  /**
   * Update dimensions
   */
  updateDimensions(dimensions, symbolWidth, symbolHeight) {
    this.dimensions = dimensions;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;

    if (this.spine) {
      this.applyScaleAndCenter();
    }
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    if (this.handlerCleanup) {
      this.handlerCleanup();
      this.handlerCleanup = null;
    }

    resetIosFullscreenState();

    // Cancel fade out animation
    if (this.fadeOutAnimationRef) {
      this.fadeOutAnimationRef.pause();
      this.fadeOutAnimationRef = null;
    }

    // Cleanup spine
    if (this.spine) {
      if (this.spine.state) {
        try {
          this.spine.state.clearListeners();
          this.spine.state.clearTracks();
        } catch (error) {
          // Silent fail
        }
      }

      try {
        this.spine.destroy();
      } catch (error) {
        // Silent fail
      }

      this.spine = null;
    }

    // Cleanup spine container
    if (this.spineContainer) {
      if (
        this.container &&
        this.container.children.includes(this.spineContainer)
      ) {
        this.container.removeChild(this.spineContainer);
      }

      try {
        this.spineContainer.destroy({ children: true });
      } catch (error) {
        // Silent fail
      }

      this.spineContainer = null;
    }

    this.initialized = false;
    this.isCleanedUp = true;
  }
}

export default IosFullscreenOverlayController;
